import {
	useImperativeHandle,
	useLayoutEffect,
	useRef,
	useState,
	type KeyboardEvent,
	type PointerEvent,
	type Ref,
} from "react";
import { Avatar } from "@/components/common/Avatar";
import { Icon } from "@/components/common/Icon";
import { Popover } from "@/components/common/Popover";
import type { RoadmapTask } from "@/lib/api";
import { formatFullName } from "@/lib/format-name";
import {
	dateOfIndex,
	dayIndex,
	dragSpan,
	LANE_HUES,
	laneKeyOf,
	monthStarts,
	packRows,
	spanBetween,
	weekdayOf,
	type BarDragMode,
	type DayRange,
	type RoadmapLane,
} from "@/lib/roadmap";

export const DAY_WIDTH_MIN = 6;
export const DAY_WIDTH_MAX = 120;
export const DAY_WIDTH_DEFAULT = 28;
// これより狭いと日付が重なるので、見出しには月曜と今日の日付だけ出す。
const DAY_LABEL_MIN_WIDTH = 20;
const ROW_HEIGHT = 36;
const BAR_HEIGHT = 24;
const BAR_TOP = (ROW_HEIGHT - BAR_HEIGHT) / 2;
const LABEL_WIDTH = 220;
const MONTH_ROW_HEIGHT = 22;
const DAY_ROW_HEIGHT = 24;
const HEADER_HEIGHT = MONTH_ROW_HEIGHT + DAY_ROW_HEIGHT;
const EDGE_WIDTH = 6;
const DRAFT_INPUT_MIN_WIDTH = 200;
const TODAY_LEAD_DAYS = 7;

type Drag =
	| { kind: "create"; laneKey: string; row: number; anchor: number; current: number }
	| {
			kind: "bar";
			taskId: string;
			mode: BarDragMode;
			origin: number;
			current: number;
			/** 移動中にポインタが乗っているレーン。離すとこのレーンの担当者になる。 */
			laneKey: string;
	  };

interface Draft {
	laneKey: string;
	row: number;
	span: DayRange;
}

export interface RoadmapGanttHandle {
	scrollToToday: () => void;
}

interface RoadmapGanttProps {
	tasks: RoadmapTask[];
	lanes: RoadmapLane[];
	range: DayRange;
	today: number;
	/** 1 日あたりの横幅 (px)。 */
	dayWidth: number;
	selectedId: string | null;
	onSelect: (id: string | null) => void;
	/** ドラッグで作ったバーに名前を付けて確定したとき。失敗時は reject し、下書きを残す。 */
	onCreate: (span: DayRange, laneKey: string, name: string) => Promise<void>;
	/** バーを動かしたとき。laneKey は離したレーン (担当者)。 */
	onChange: (task: RoadmapTask, span: DayRange, laneKey: string) => void;
	/** レーンの色をユーザーが選んだか。選んでいれば既定の色に戻せる。 */
	laneHueChosen: (laneKey: string) => boolean;
	/** レーンの色を選んだとき。null は既定の色に戻す。 */
	onLaneHueChange: (laneKey: string, hue: number | null) => void;
	ref?: Ref<RoadmapGanttHandle>;
}

/**
 * ロードマップのガントチャート。担当者ごとのレーンに、期間の重なるタスクを行に積んで並べる。
 * レーンの空いた所をドラッグするとそのレーンの担当者でタスクの下書きができ、そのバーで名前を入力して確定する。
 * バーは本体のドラッグで移動 (別のレーンへ動かすと担当者が変わる)、両端のドラッグで伸縮する。
 */
export function RoadmapGantt({
	tasks,
	lanes,
	range,
	today,
	dayWidth,
	selectedId,
	onSelect,
	onCreate,
	onChange,
	laneHueChosen,
	onLaneHueChange,
	ref,
}: RoadmapGanttProps) {
	const scrollRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const laneRefs = useRef(new Map<string, HTMLDivElement>());
	const barRefs = useRef(new Map<string, HTMLDivElement>());
	const [drag, setDrag] = useState<Drag | null>(null);
	const [draft, setDraft] = useState<Draft | null>(null);
	const days = range.end - range.start + 1;
	// 見出しの日付の真ん中をその日の始まりとし、バー・ドラッグ・週末の帯をすべて半日ずらす。
	const dayStartOffset = dayWidth / 2;

	const scrollToToday = () => {
		scrollRef.current?.scrollTo({ left: (today - range.start - TODAY_LEAD_DAYS) * dayWidth });
	};
	useImperativeHandle(ref, () => ({ scrollToToday }));

	const scrollToTask = (task: RoadmapTask) => {
		const el = scrollRef.current;
		const bar = barRefs.current.get(task.id);
		if (el === null || bar === undefined) return;
		el.scrollTo({
			left: (dayIndex(task.startDate) - range.start - TODAY_LEAD_DAYS) * dayWidth,
			top:
				bar.getBoundingClientRect().top -
				el.getBoundingClientRect().top +
				el.scrollTop -
				HEADER_HEIGHT -
				ROW_HEIGHT,
		});
	};

	// リンクでタスクを開いたときはそのバーを、それ以外は今日を見せる。
	const scrolledInitially = useRef(false);
	useLayoutEffect(() => {
		if (scrolledInitially.current) return;
		scrolledInitially.current = true;
		const task = tasks.find((t) => t.id === selectedId);
		if (task === undefined) scrollToToday();
		else scrollToTask(task);
	});

	// 幅を変えても、表示中の真ん中の日が真ん中に残るようにスクロールし直す。
	const prevDayWidth = useRef(dayWidth);
	useLayoutEffect(() => {
		const el = scrollRef.current;
		const prev = prevDayWidth.current;
		prevDayWidth.current = dayWidth;
		if (el === null || prev === dayWidth) return;
		const half = (el.clientWidth - LABEL_WIDTH) / 2;
		const centerDays = (el.scrollLeft + half - prev / 2) / prev;
		el.scrollLeft = centerDays * dayWidth + dayWidth / 2 - half;
	}, [dayWidth]);

	const dayAt = (clientX: number) => {
		const left = contentRef.current?.getBoundingClientRect().left ?? 0;
		const day =
			range.start + Math.floor((clientX - left - LABEL_WIDTH - dayStartOffset) / dayWidth);
		return Math.min(Math.max(day, range.start), range.end);
	};

	const laneAt = (clientY: number) => {
		for (const [key, el] of laneRefs.current) {
			const rect = el.getBoundingClientRect();
			if (clientY >= rect.top && clientY < rect.bottom) return key;
		}
		return null;
	};

	const startCreate = (e: PointerEvent<HTMLDivElement>, laneKey: string) => {
		if (e.button !== 0 || draft !== null) return;
		e.currentTarget.setPointerCapture(e.pointerId);
		const row = Math.floor((e.clientY - e.currentTarget.getBoundingClientRect().top) / ROW_HEIGHT);
		const day = dayAt(e.clientX);
		setDrag({ kind: "create", laneKey, row, anchor: day, current: day });
	};

	const startBarDrag = (e: PointerEvent<HTMLDivElement>, task: RoadmapTask, laneKey: string) => {
		if (e.button !== 0) return;
		e.stopPropagation();
		e.currentTarget.setPointerCapture(e.pointerId);
		const rect = e.currentTarget.getBoundingClientRect();
		const offset = e.clientX - rect.left;
		const mode: BarDragMode =
			offset < EDGE_WIDTH
				? "resize-start"
				: offset > rect.width - EDGE_WIDTH
					? "resize-end"
					: "move";
		const day = dayAt(e.clientX);
		setDrag({ kind: "bar", taskId: task.id, mode, origin: day, current: day, laneKey });
	};

	const moveDrag = (e: PointerEvent<HTMLDivElement>) => {
		if (drag === null) return;
		const day = dayAt(e.clientX);
		const laneKey =
			drag.kind === "bar" && drag.mode === "move"
				? (laneAt(e.clientY) ?? drag.laneKey)
				: drag.laneKey;
		if (day !== drag.current || laneKey !== drag.laneKey)
			setDrag({ ...drag, current: day, laneKey });
	};

	// タッチのスクロールや OS のジェスチャでポインタを奪われたときは、途中の位置を保存せずに戻す。
	const cancelDrag = () => setDrag(null);

	const endDrag = () => {
		if (drag === null) return;
		setDrag(null);
		if (drag.kind === "create") {
			// ドラッグせずに離したときは選択を外すだけにする。
			if (drag.anchor === drag.current) onSelect(null);
			else
				setDraft({
					laneKey: drag.laneKey,
					row: drag.row,
					span: spanBetween(drag.anchor, drag.current),
				});
			return;
		}
		const task = tasks.find((t) => t.id === drag.taskId);
		if (task === undefined) return;
		if (drag.current === drag.origin && drag.laneKey === laneKeyOf(task, lanes)) {
			onSelect(task.id);
			return;
		}
		onChange(
			task,
			dragSpan(spanOf(task), drag.mode, drag.current - drag.origin, range),
			drag.laneKey,
		);
	};

	const displayedSpan = (task: RoadmapTask): DayRange =>
		drag?.kind === "bar" && drag.taskId === task.id
			? dragSpan(spanOf(task), drag.mode, drag.current - drag.origin, range)
			: spanOf(task);

	const createPreview: Draft | null =
		drag?.kind === "create" && drag.anchor !== drag.current
			? { laneKey: drag.laneKey, row: drag.row, span: spanBetween(drag.anchor, drag.current) }
			: draft;

	const draggedTask = drag?.kind === "bar" ? tasks.find((t) => t.id === drag.taskId) : undefined;
	const dropLaneKey =
		drag?.kind === "bar" &&
		draggedTask !== undefined &&
		drag.laneKey !== laneKeyOf(draggedTask, lanes)
			? drag.laneKey
			: null;

	return (
		<div
			ref={scrollRef}
			data-testid="roadmap-gantt"
			style={{ flex: 1, minHeight: 0, overflow: "auto", position: "relative" }}
		>
			<div
				ref={contentRef}
				style={{
					position: "relative",
					width: LABEL_WIDTH + days * dayWidth + dayStartOffset,
					minHeight: "100%",
					display: "flex",
					flexDirection: "column",
				}}
			>
				<TimelineHeader range={range} today={today} dayWidth={dayWidth} />
				<TimelineBackground range={range} today={today} dayWidth={dayWidth} />

				{lanes.map((lane) => {
					const laneTasks = tasks.filter((t) => laneKeyOf(t, lanes) === lane.key);
					const { rowOf, rowCount } = packRows(laneTasks);
					const preview = createPreview?.laneKey === lane.key ? createPreview : null;
					const rows = Math.max(rowCount, (preview?.row ?? 0) + 1);
					return (
						<div
							key={lane.key}
							ref={(el) => {
								if (el) laneRefs.current.set(lane.key, el);
								else laneRefs.current.delete(lane.key);
							}}
							data-testid={`roadmap-lane-${lane.key || "unassigned"}`}
							style={{
								display: "flex",
								flex: "none",
								height: rows * ROW_HEIGHT,
								position: "relative",
								borderBottom: "1px solid var(--line-1)",
								background: dropLaneKey === lane.key ? "var(--row-dragover-bg)" : undefined,
							}}
						>
							<LaneLabel
								lane={lane}
								hueChosen={laneHueChosen(lane.key)}
								onHueChange={(hue) => onLaneHueChange(lane.key, hue)}
							/>
							<div
								onPointerDown={(e) => startCreate(e, lane.key)}
								onPointerMove={moveDrag}
								onPointerUp={endDrag}
								onPointerCancel={cancelDrag}
								style={{
									position: "relative",
									flex: 1,
									cursor: draft === null ? "crosshair" : undefined,
									touchAction: "none",
								}}
							>
								{laneTasks.map((task) => (
									<TaskBar
										key={task.id}
										ref={(el) => {
											if (el) barRefs.current.set(task.id, el);
											else barRefs.current.delete(task.id);
										}}
										task={task}
										hue={lane.hue}
										span={displayedSpan(task)}
										range={range}
										dayWidth={dayWidth}
										top={(rowOf.get(task.id) ?? 0) * ROW_HEIGHT + BAR_TOP}
										selected={task.id === selectedId}
										onPointerDown={(e) => startBarDrag(e, task, lane.key)}
										onPointerMove={moveDrag}
										onPointerUp={endDrag}
										onPointerCancel={cancelDrag}
									/>
								))}
								{preview !== null && (
									<DraftBar
										span={preview.span}
										range={range}
										dayWidth={dayWidth}
										top={preview.row * ROW_HEIGHT + BAR_TOP}
										editing={draft !== null}
										onCommit={(name) =>
											onCreate(preview.span, preview.laneKey, name).then(() => setDraft(null))
										}
										onCancel={() => setDraft(null)}
									/>
								)}
							</div>
						</div>
					);
				})}

				{/* レーンが少なくても左の列を下端まで伸ばす。 */}
				<div style={{ flex: 1, display: "flex" }}>
					<div style={{ ...labelCellStyle, height: "auto" }} />
					<div style={{ flex: 1 }} onClick={() => onSelect(null)} />
				</div>
			</div>
		</div>
	);
}

function LaneLabel({
	lane,
	hueChosen,
	onHueChange,
}: {
	lane: RoadmapLane;
	hueChosen: boolean;
	onHueChange: (hue: number | null) => void;
}) {
	const name = lane.assignee === null ? "未割り当て" : formatFullName(lane.assignee);
	return (
		<div style={{ ...labelCellStyle, alignItems: "flex-start" }}>
			<div
				style={{
					height: ROW_HEIGHT,
					minWidth: 0,
					display: "flex",
					alignItems: "center",
					gap: 8,
					color: lane.assignee?.active === false ? "var(--fg-3)" : "var(--fg-1)",
				}}
			>
				{lane.assignee !== null && (
					<Avatar firstName={lane.assignee.firstName} lastName={lane.assignee.lastName} size={20} />
				)}
				<span
					title={name}
					data-tooltip-if-truncated
					style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
				>
					{name}
					{lane.assignee?.active === false && " (無効)"}
				</span>
				{lane.hue !== null && (
					<LaneHuePicker
						laneKey={lane.key}
						hue={lane.hue}
						chosen={hueChosen}
						onChange={onHueChange}
					/>
				)}
			</div>
		</div>
	);
}

const SWATCH_SIZE = 20;
const HUE_PICKER_PADDING = 8;
const HUE_PICKER_GAP = 6;
const HUE_PICKER_COLUMNS = 4;

function laneColor(hue: number, alpha = 1) {
	return `hsl(${hue} var(--segment-saturation) var(--segment-lightness) / ${alpha})`;
}

function LaneHuePicker({
	laneKey,
	hue,
	chosen,
	onChange,
}: {
	laneKey: string;
	hue: number;
	chosen: boolean;
	onChange: (hue: number | null) => void;
}) {
	const [open, setOpen] = useState(false);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const choose = (next: number | null) => {
		onChange(next);
		setOpen(false);
	};
	return (
		<>
			<button
				ref={buttonRef}
				type="button"
				title="色を変える"
				data-testid={`roadmap-lane-color-${laneKey}`}
				onClick={() => setOpen((v) => !v)}
				style={{
					flex: "none",
					width: 14,
					height: 14,
					padding: 0,
					border: "none",
					borderRadius: "var(--radius-full)",
					background: laneColor(hue),
					cursor: "pointer",
				}}
			/>
			{open && (
				<Popover
					anchorRef={buttonRef}
					onClose={() => setOpen(false)}
					anchored
					align="right"
					width={
						HUE_PICKER_COLUMNS * SWATCH_SIZE +
						(HUE_PICKER_COLUMNS - 1) * HUE_PICKER_GAP +
						2 * HUE_PICKER_PADDING
					}
					padding={HUE_PICKER_PADDING}
					testId="roadmap-lane-color-picker"
				>
					<div
						style={{
							display: "grid",
							gridTemplateColumns: `repeat(${HUE_PICKER_COLUMNS}, ${SWATCH_SIZE}px)`,
							gap: HUE_PICKER_GAP,
						}}
					>
						{LANE_HUES.map((option) => (
							<button
								key={option}
								type="button"
								data-testid={`roadmap-lane-color-option-${option}`}
								onClick={() => choose(option)}
								style={{
									width: SWATCH_SIZE,
									height: SWATCH_SIZE,
									padding: 0,
									border: "none",
									borderRadius: "var(--radius-full)",
									background: laneColor(option),
									boxShadow:
										option === hue
											? "0 0 0 2px var(--surface-1), 0 0 0 4px var(--fg-1)"
											: undefined,
									cursor: "pointer",
								}}
							/>
						))}
					</div>
					{chosen && (
						<button
							type="button"
							data-testid="roadmap-lane-color-reset"
							onClick={() => choose(null)}
							style={{
								marginTop: HUE_PICKER_PADDING,
								width: "100%",
								padding: "4px 0",
								border: "none",
								borderRadius: "var(--radius-2)",
								background: "none",
								color: "var(--fg-2)",
								fontSize: "var(--text-caption)",
								cursor: "pointer",
							}}
						>
							既定に戻す
						</button>
					)}
				</Popover>
			)}
		</>
	);
}

function spanOf(task: RoadmapTask): DayRange {
	return { start: dayIndex(task.startDate), end: dayIndex(task.endDate) };
}

function barGeometry(span: DayRange, range: DayRange, dayWidth: number) {
	return {
		left: (span.start - range.start) * dayWidth + dayWidth / 2,
		width: (span.end - span.start + 1) * dayWidth,
	};
}

const labelCellStyle = {
	position: "sticky",
	left: 0,
	zIndex: 2,
	flex: "none",
	width: LABEL_WIDTH,
	height: "100%",
	display: "flex",
	alignItems: "center",
	padding: "0 12px",
	boxSizing: "border-box",
	background: "var(--surface-1)",
	borderRight: "1px solid var(--line-1)",
	fontSize: "var(--text-list)",
	fontFamily: "var(--font-sans)",
} as const;

function TaskBar({
	ref,
	task,
	hue,
	span,
	range,
	dayWidth,
	top,
	selected,
	onPointerDown,
	onPointerMove,
	onPointerUp,
	onPointerCancel,
}: {
	ref: Ref<HTMLDivElement>;
	task: RoadmapTask;
	hue: number | null;
	span: DayRange;
	range: DayRange;
	dayWidth: number;
	top: number;
	selected: boolean;
	onPointerDown: (e: PointerEvent<HTMLDivElement>) => void;
	onPointerMove: (e: PointerEvent<HTMLDivElement>) => void;
	onPointerUp: () => void;
	onPointerCancel: () => void;
}) {
	const { left, width } = barGeometry(span, range, dayWidth);
	const { background, line } = barColors(hue, selected);
	return (
		<div
			ref={ref}
			data-testid={`roadmap-bar-${task.id}`}
			data-tooltip={`${task.name}
${dateOfIndex(span.start)} 〜 ${dateOfIndex(span.end)}${task.done ? "（完了）" : ""}`}
			data-tooltip-delay={0}
			onPointerDown={onPointerDown}
			onPointerMove={onPointerMove}
			onPointerUp={onPointerUp}
			onPointerCancel={onPointerCancel}
			style={{
				position: "absolute",
				top,
				left: left + 1,
				width: width - 2,
				height: BAR_HEIGHT,
				display: "flex",
				alignItems: "center",
				gap: 6,
				padding: `0 ${EDGE_WIDTH}px`,
				boxSizing: "border-box",
				borderRadius: "var(--radius-2)",
				background,
				boxShadow: `inset 0 0 0 ${selected ? 2 : 1}px ${line}`,
				color: selected ? "var(--fg-0)" : "var(--fg-1)",
				fontSize: "var(--text-caption)",
				cursor: "grab",
				userSelect: "none",
				touchAction: "none",
				overflow: "hidden",
				whiteSpace: "nowrap",
				opacity: task.done && !selected ? 0.55 : 1,
			}}
		>
			<EdgeHandle side="left" />
			{task.done && <Icon name="check" size={12} stroke={2} />}
			<span
				style={{
					overflow: "hidden",
					textOverflow: "ellipsis",
					textDecoration: task.done ? "line-through" : "none",
				}}
			>
				{task.name}
			</span>
			<EdgeHandle side="right" />
		</div>
	);
}

function barColors(hue: number | null, selected: boolean) {
	if (hue === null) {
		return {
			background: selected ? "var(--neutral-solid-hover)" : "var(--neutral-solid)",
			line: selected ? "var(--fg-2)" : "var(--fg-4)",
		};
	}
	return {
		background: laneColor(hue, selected ? 0.5 : 0.22),
		line: laneColor(hue, selected ? 1 : 0.6),
	};
}

function EdgeHandle({ side }: { side: "left" | "right" }) {
	return (
		<div
			style={{
				position: "absolute",
				top: 0,
				bottom: 0,
				[side]: 0,
				width: EDGE_WIDTH,
				cursor: "ew-resize",
			}}
		/>
	);
}

function DraftBar({
	span,
	range,
	dayWidth,
	top,
	editing,
	onCommit,
	onCancel,
}: {
	span: DayRange;
	range: DayRange;
	dayWidth: number;
	top: number;
	editing: boolean;
	onCommit: (name: string) => Promise<void>;
	onCancel: () => void;
}) {
	const [name, setName] = useState("");
	const [saving, setSaving] = useState(false);
	// Escape や確定で外れたあとに blur が来ても、もう一度確定しない。
	const closed = useRef(false);
	const { left, width } = barGeometry(span, range, dayWidth);

	const cancel = () => {
		closed.current = true;
		onCancel();
	};

	const commit = () => {
		if (saving || closed.current) return;
		const trimmed = name.trim();
		if (trimmed === "") {
			cancel();
			return;
		}
		setSaving(true);
		// 失敗はページ側で表示済み。下書きを残して入力し直せるようにする。
		onCommit(trimmed).catch((err: unknown) => {
			console.error(err);
			setSaving(false);
		});
	};

	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.nativeEvent.isComposing) return;
		if (e.key === "Enter") commit();
		if (e.key === "Escape") cancel();
	};

	return (
		<div
			data-testid="roadmap-draft-bar"
			onPointerDown={(e) => e.stopPropagation()}
			style={{
				position: "absolute",
				top,
				left: left + 1,
				width: editing ? Math.max(width - 2, DRAFT_INPUT_MIN_WIDTH) : width - 2,
				height: BAR_HEIGHT,
				borderRadius: "var(--radius-2)",
				background: "var(--accent-subtle)",
				boxShadow: "inset 0 0 0 1px var(--accent)",
				display: "flex",
				alignItems: "center",
			}}
		>
			{editing && (
				<input
					autoFocus
					data-testid="roadmap-draft-name"
					value={name}
					disabled={saving}
					placeholder="タスク名を入力して Enter"
					onChange={(e) => setName(e.target.value)}
					onKeyDown={onKeyDown}
					onBlur={commit}
					style={{
						width: "100%",
						height: "100%",
						padding: `0 ${EDGE_WIDTH}px`,
						border: "none",
						outline: "none",
						background: "transparent",
						color: "var(--fg-1)",
						fontSize: "var(--text-caption)",
						fontFamily: "var(--font-sans)",
					}}
				/>
			)}
		</div>
	);
}

interface TimelineProps {
	range: DayRange;
	today: number;
	dayWidth: number;
}

function TimelineHeader({ range, today, dayWidth }: TimelineProps) {
	const days = range.end - range.start + 1;
	return (
		<div
			style={{
				position: "sticky",
				top: 0,
				zIndex: 3,
				display: "flex",
				flex: "none",
				height: HEADER_HEIGHT,
				background: "var(--surface-1)",
				borderBottom: "1px solid var(--line-1)",
			}}
		>
			<div
				style={{
					...labelCellStyle,
					zIndex: 4,
					alignItems: "flex-end",
					paddingBottom: 6,
					color: "var(--fg-3)",
					fontSize: "var(--text-caption)",
				}}
			>
				タスク
			</div>
			<div style={{ position: "relative", width: days * dayWidth + dayWidth / 2, flex: "none" }}>
				{/* 月の幅の枠の中でラベルを左端に貼り付け、次の月の枠が来たら押し出されるようにする。 */}
				{monthStarts(range).map((m, i, months) => (
					<div
						key={m.index}
						style={{
							position: "absolute",
							left: (m.index - range.start) * dayWidth,
							width: ((months[i + 1]?.index ?? range.end + 1) - m.index) * dayWidth,
							top: 0,
							height: MONTH_ROW_HEIGHT,
							display: "flex",
							alignItems: "center",
						}}
					>
						<span
							style={{
								position: "sticky",
								left: LABEL_WIDTH,
								paddingLeft: 6,
								fontSize: "var(--text-caption)",
								fontWeight: 600,
								color: "var(--fg-2)",
								whiteSpace: "nowrap",
							}}
						>
							{m.label}
						</span>
					</div>
				))}
				<div style={{ position: "absolute", top: MONTH_ROW_HEIGHT, left: 0, display: "flex" }}>
					{Array.from({ length: days }, (_, i) => {
						const index = range.start + i;
						const weekday = weekdayOf(index);
						const isToday = index === today;
						const labeled = dayWidth >= DAY_LABEL_MIN_WIDTH || weekday === 1 || isToday;
						return (
							<div
								key={index}
								style={{
									width: dayWidth,
									height: DAY_ROW_HEIGHT,
									display: "grid",
									placeItems: "center",
									fontSize: "var(--text-section)",
									fontVariantNumeric: "tabular-nums",
									color: isToday
										? "var(--on-accent-solid)"
										: weekday === 0 || weekday === 6
											? "var(--fg-4)"
											: "var(--fg-3)",
								}}
							>
								<span
									style={{
										minWidth: 18,
										padding: "2px 0",
										textAlign: "center",
										borderRadius: "var(--radius-full)",
										background: isToday ? "var(--accent-solid)" : undefined,
									}}
								>
									{labeled && Number(dateOfIndex(index).slice(8))}
								</span>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}

function TimelineBackground({ range, today, dayWidth }: TimelineProps) {
	const days = range.end - range.start + 1;
	return (
		<div
			style={{
				position: "absolute",
				top: HEADER_HEIGHT,
				bottom: 0,
				left: LABEL_WIDTH,
				width: days * dayWidth + dayWidth / 2,
				pointerEvents: "none",
			}}
		>
			{Array.from({ length: days }, (_, i) => {
				const index = range.start + i;
				if (weekdayOf(index) === 6) {
					return (
						<div
							key={index}
							style={{
								position: "absolute",
								top: 0,
								bottom: 0,
								left: i * dayWidth + dayWidth / 2,
								width: dayWidth * 2,
								background: "var(--surface-0)",
							}}
						/>
					);
				}
				return null;
			})}
			<div
				style={{
					position: "absolute",
					top: 0,
					bottom: 0,
					left: (today - range.start) * dayWidth + dayWidth / 2 - 1,
					width: 2,
					background: "var(--accent)",
				}}
			/>
		</div>
	);
}
